import { Box, type BoxProps, Flex, type FlexProps } from '@chakra-ui/react';

import { TextLabel, TextLabelSmall } from 'uiToolkitV1/Typography';

import { minifyNumber } from '../../../constants/numberMinifyHelper';

type BudgetBarChartProps = {
  totalBudget: number;
  currentComp: number;
  proposedComp: number;
  targetBudget: number;
  step?: number;
  showLegend?: boolean;
  showAxisLabels?: boolean;
  showPriceLabel?: boolean;
  wrapperProps?: FlexProps;
  chartContainerProps?: BoxProps;
};

export const BudgetBarChart = ({
  totalBudget,
  currentComp,
  proposedComp,
  targetBudget,
  step = 5,
  showLegend = true,
  showAxisLabels = true,
  showPriceLabel = true,
  wrapperProps,
  chartContainerProps,
}: BudgetBarChartProps) => {
  const formatCurrency = (amount: number) => {
    if (amount >= 10000) {
      return `$${minifyNumber(amount, {
        precision: 1,
        lowercase: false,
        space: false,
        units: ['', 'K', 'M', 'B', 'T', 'P', 'E'],
        locales: 'en-US',
      })}`;
    }

    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const getPercentage = (value: number) => (value / totalBudget) * 100;

  const currentWidth = getPercentage(currentComp);
  const proposedWidth = getPercentage(proposedComp);
  const targetPosition = getPercentage(targetBudget);
  const unusedStart = currentWidth + proposedWidth;
  const unusedWidth = 100 - unusedStart;
  const unusedBudget = totalBudget - currentComp - proposedComp;

  // Generate axis labels based on total budget and step
  const generateAxisLabels = () => {
    const labels: string[] = [];
    const stepAmount = totalBudget / step;

    for (let i = 0; i <= step; i += 1) {
      const value = Math.round(i * stepAmount);

      if (value >= 1000) {
        labels.push(
          minifyNumber(value, {
            precision: 1,
            lowercase: false,
            space: false,
            units: ['', 'K', 'M', 'B', 'T', 'P', 'E'],
            locales: 'en-US',
          })
        );
      } else {
        labels.push(
          value.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
        );
      }
    }

    return labels;
  };

  const axisLabels = generateAxisLabels();

  return (
    <Flex flexDir="column" justify="space-between" w="100%" h="100%" bg="white" {...wrapperProps}>
      <Box>
        {/* Chart Container */}
        <Box position="relative" h="32px" bg="neutral.F2F2F2" borderRadius="4px" zIndex={1} {...chartContainerProps}>
          {/* Current Compensation */}
          <Flex
            position="absolute"
            top={0}
            left={0}
            h="100%"
            w={`${currentWidth}%`}
            bg="functional.DBF2D3"
            align="center"
            justify="center"
            color="text.g1"
            fontSize="16px"
            fontWeight="600"
            borderTopLeftRadius="4px"
            borderBottomLeftRadius="4px"
          >
            {showPriceLabel && formatCurrency(currentComp)}
          </Flex>

          {/* Proposed Compensation */}
          <Flex
            position="absolute"
            top={0}
            left={`${currentWidth}%`}
            h="100%"
            w={`${proposedWidth}%`}
            bg="functional.FFEFD4"
            align="center"
            justify="center"
            color="text.g1"
            fontSize="16px"
            fontWeight="600"
          >
            {showPriceLabel && formatCurrency(proposedComp)}
          </Flex>

          {/* Unused Budget */}
          {unusedWidth > 0 && (
            <Flex
              position="absolute"
              top={0}
              left={`${unusedStart}%`}
              h="100%"
              w={`${unusedWidth}%`}
              bg="neutral.F2F2F2"
              align="center"
              justify="flex-end"
              color="text.g1"
              fontSize="16px"
              fontWeight="600"
              pr={showPriceLabel ? '8px' : '0px'}
              borderTopRightRadius="4px"
              borderBottomRightRadius="4px"
            >
              {showPriceLabel && formatCurrency(unusedBudget)}
            </Flex>
          )}

          {/* Target Budget Line */}
          <Box
            position="absolute"
            top="-4px"
            left={`${targetPosition}%`}
            transform="translateX(-50%)"
            h="calc(100% + 8px)"
            borderLeft="2px solid"
            borderColor="functional.C62C02"
          />
        </Box>

        {/* X-axis labels */}
        {showAxisLabels && (
          <Box position="relative" mt="24px">
            <Box w="100%" h="1px" bg="neutral.CFD7DF" mb="8px" />
            {axisLabels.map((label, index) => {
              const position = (index / step) * 100;
              const isFirst = index === 0;
              const isLast = index === step;

              // Determine transform based on position
              let transform = 'translateX(-50%)'; // Default: center
              if (isFirst) {
                transform = 'translateX(0%)'; // Left align first label
              } else if (isLast) {
                transform = 'translateX(-100%)'; // Right align last label
              }

              return (
                <>
                  <Box
                    position="absolute"
                    left={`${position}%`}
                    top="-80px"
                    h="80px"
                    w="1px"
                    transform="translateX(-50%)"
                    bg={index === 0 ? 'neutral.CFD7DF' : 'neutral.F5F7F9'}
                  />

                  <TextLabelSmall
                    key={label}
                    position="absolute"
                    left={`${position}%`}
                    transform={transform}
                    fontWeight="590"
                    color="text.g2"
                    whiteSpace="nowrap"
                  >
                    {label}
                  </TextLabelSmall>
                </>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Legend */}
      {showLegend && (
        <Flex wrap="wrap" gap="12px" fontSize="14px">
          <Flex align="center" gap="4px">
            <Box
              w="12px"
              h="12px"
              bg="functional.DBF2D3"
              borderRadius="50%"
              border="1px solid"
              borderColor="functional.44812F"
            />
            <TextLabel>Current comp</TextLabel>
          </Flex>
          <Flex align="center" gap="4px">
            <Box
              w="12px"
              h="12px"
              bg="functional.FFEFD4"
              borderRadius="50%"
              border="1px solid"
              borderColor="functional.8C6A00"
            />
            <TextLabel>Proposed comp</TextLabel>
          </Flex>
          <Flex align="center" gap="4px">
            <Box
              w="12px"
              h="12px"
              bg="feedback.FFE1D9"
              borderRadius="50%"
              border="1px solid"
              borderColor="functional.C62C02"
            />
            <TextLabel>Target budget</TextLabel>
          </Flex>
          <Flex align="center" gap="4px">
            <Box w="12px" h="12px" bg="neutral.F2F2F2" borderRadius="50%" border="1px solid" borderColor="text.g3" />
            <TextLabel>Unused budget</TextLabel>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
